import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Target,
  Search,
  Sparkles,
  CheckCircle2,
  XCircle,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Loader2,
  Trash2,
  Plus,
  Filter,
  TrendingUp,
  TrendingDown,
  Eye,
  MousePointerClick,
  Hash,
  Plug,
  AlertCircle,
  RefreshCw,
  ShieldCheck,
  Globe,
  Database,
  ExternalLink,
  BarChart3,
  Zap,
  MapPin,
  HelpCircle,
  Video,
  ImageIcon,
  Star,
  DollarSign,
  Activity,
  ChevronRight,
  X,
  FileText,
  History,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { api } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import { useTaskState } from '../../context/TaskStateContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';

const getErrorMessage = (err, fallback = 'Request failed') => {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || String(item)).join(', ');
  }
  if (detail && typeof detail === 'object') {
    return detail.message || detail.error || fallback;
  }
  return err.message || fallback;
};

const STATUS_CONFIG = {
  tracking: { label: 'Tracking', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  suggested: { label: 'Suggested', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const INTENT_CONFIG = {
  informational: { label: 'Informational', color: 'bg-sky-100 text-sky-700' },
  transactional: { label: 'Transactional', color: 'bg-emerald-100 text-emerald-700' },
  navigational: { label: 'Navigational', color: 'bg-violet-100 text-violet-700' },
  commercial: { label: 'Commercial', color: 'bg-amber-100 text-amber-700' },
};

function PositionBadge({ position }) {
  if (!position && position !== 0) return <span className="text-slate-400">--</span>;
  const rounded = Math.round(position);
  const color =
    position <= 3 ? 'text-emerald-600 bg-emerald-50' :
    position <= 10 ? 'text-blue-600 bg-blue-50' :
    position <= 20 ? 'text-amber-600 bg-amber-50' :
    'text-slate-600 bg-slate-50';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${color}`}>
      #{rounded}
    </span>
  );
}

function OverviewCard({ icon: Icon, label, value, subtext, color }) {
  return (
    <div className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <p className="text-xs text-slate-500 font-medium">{label}</p>
      </div>
      <p className="text-2xl font-bold font-heading">{value}</p>
      {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
    </div>
  );
}

function PositionBucketBar({ buckets }) {
  const total = Object.values(buckets).reduce((a, b) => a + b, 0);
  if (!total) return null;
  const items = [
    { key: '1', label: '#1', color: 'bg-emerald-500' },
    { key: '2-3', label: '#2-3', color: 'bg-emerald-400' },
    { key: '4-10', label: '#4-10', color: 'bg-blue-500' },
    { key: '11-20', label: '#11-20', color: 'bg-amber-500' },
    { key: '21-50', label: '#21-50', color: 'bg-orange-500' },
    { key: '51-100', label: '#51-100', color: 'bg-red-400' },
    { key: '100+', label: '100+', color: 'bg-slate-400' },
  ];
  return (
    <div className="space-y-3">
      <div className="flex h-6 rounded-full overflow-hidden">
        {items.map(({ key, color }) => {
          const pct = (buckets[key] || 0) / total * 100;
          if (!pct) return null;
          return <div key={key} className={`${color} transition-all`} style={{ width: `${pct}%` }} />;
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {items.map(({ key, label, color }) => (
          <div key={key} className="flex items-center gap-1.5 text-xs">
            <span className={`w-2.5 h-2.5 rounded-full ${color}`} />
            <span className="text-slate-600 dark:text-slate-400">{label}</span>
            <span className="font-semibold">{buckets[key] || 0}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SerpFeatureBadges({ features }) {
  if (!features) return null;
  const items = [
    { key: 'featured_snippet', label: 'Featured Snippet', icon: Star, color: 'bg-amber-100 text-amber-700' },
    { key: 'local_pack', label: 'Local Pack', icon: MapPin, color: 'bg-blue-100 text-blue-700' },
    { key: 'people_also_ask', label: 'People Also Ask', icon: HelpCircle, color: 'bg-violet-100 text-violet-700' },
    { key: 'video_results', label: 'Video', icon: Video, color: 'bg-red-100 text-red-700' },
    { key: 'image_results', label: 'Images', icon: ImageIcon, color: 'bg-cyan-100 text-cyan-700' },
  ];
  const active = items.filter(i => features[i.key]);
  if (!active.length) return <span className="text-xs text-slate-400">None detected</span>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {active.map(({ key, label, icon: Icon, color }) => (
        <span key={key} className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${color}`}>
          <Icon className="w-3 h-3" />{label}
        </span>
      ))}
    </div>
  );
}

function KeywordDetailPanel({ keyword: kw, onClose }) {
  if (!kw) return null;
  const pos = kw.current_position;
  const posConfig = pos ? (pos <= 3 ? 'text-emerald-600' : pos <= 10 ? 'text-blue-600' : pos <= 20 ? 'text-amber-600' : 'text-slate-600') : 'text-slate-400';

  return (
    <motion.div
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className="fixed inset-y-0 right-0 w-full max-w-lg bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 shadow-2xl z-50 overflow-y-auto"
    >
      <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 p-4 flex items-center justify-between z-10">
        <h2 className="font-heading font-bold text-lg truncate">{kw.keyword}</h2>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="p-5 space-y-6">
        {/* Position + Traffic Hero */}
        <div className="grid grid-cols-3 gap-3">
          <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800 text-center">
            <p className="text-xs text-slate-500 mb-1">Position</p>
            <p className={`text-3xl font-bold ${posConfig}`}>
              {pos ? `#${Math.round(pos)}` : '--'}
            </p>
            {kw.position_change ? (
              <p className={`text-xs mt-1 font-medium ${kw.position_change > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {kw.position_change > 0 ? '▲' : '▼'} {Math.abs(kw.position_change)} pos
              </p>
            ) : null}
          </div>
          <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800 text-center">
            <p className="text-xs text-slate-500 mb-1">Est. Traffic</p>
            <p className="text-3xl font-bold text-indigo-600">{(kw.estimated_traffic || 0).toLocaleString()}</p>
            <p className="text-xs mt-1 flex items-center justify-center gap-0.5">
              {kw.traffic_source === 'gsc' ? (
                <span className="text-emerald-600"><ShieldCheck className="w-3 h-3 inline" /> GSC CTR</span>
              ) : (
                <span className="text-slate-400">estimated</span>
              )}
            </p>
          </div>
          <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800 text-center">
            <p className="text-xs text-slate-500 mb-1">Volume</p>
            <p className="text-3xl font-bold">{(kw.search_volume || 0).toLocaleString()}</p>
            {kw.volume_source && kw.volume_source !== 'ai_estimate' && (
              <p className="text-xs text-emerald-600 mt-1 flex items-center justify-center gap-0.5"><ShieldCheck className="w-3 h-3" />Verified</p>
            )}
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-3">
          {kw.difficulty != null && (
            <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 mb-1">Difficulty</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                  <div className={`h-full rounded-full ${kw.difficulty <= 30 ? 'bg-emerald-500' : kw.difficulty <= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${kw.difficulty}%` }} />
                </div>
                <span className="text-sm font-bold">{kw.difficulty}/100</span>
              </div>
            </div>
          )}
          {kw.competition && (
            <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 mb-1">Competition</p>
              <Badge className={`${kw.competition === 'low' ? 'bg-emerald-100 text-emerald-700' : kw.competition === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                {kw.competition.charAt(0).toUpperCase() + kw.competition.slice(1)}
              </Badge>
            </div>
          )}
          {kw.cpc_estimate != null && (
            <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 mb-1">Est. CPC</p>
              <p className="text-lg font-bold flex items-center gap-1"><DollarSign className="w-4 h-4 text-emerald-500" />{kw.cpc_estimate}</p>
            </div>
          )}
          {kw.gsc_ctr != null && (
            <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 mb-1">CTR</p>
              <p className="text-lg font-bold">{kw.gsc_ctr}%</p>
            </div>
          )}
          {kw.gsc_clicks != null && (
            <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 mb-1">GSC Clicks</p>
              <p className="text-lg font-bold">{kw.gsc_clicks.toLocaleString()}</p>
            </div>
          )}
          {kw.gsc_impressions != null && (
            <div className="p-3 rounded-lg border border-slate-200 dark:border-slate-700">
              <p className="text-xs text-slate-500 mb-1">Impressions</p>
              <p className="text-lg font-bold">{kw.gsc_impressions.toLocaleString()}</p>
            </div>
          )}
        </div>

        {/* Ranking URL */}
        {kw.ranking_url && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">Ranking URL</p>
            <a href={kw.ranking_url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 p-3 rounded-lg bg-indigo-50 dark:bg-indigo-950/20 text-indigo-700 dark:text-indigo-400 text-sm hover:bg-indigo-100 transition-colors truncate">
              <ExternalLink className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{kw.ranking_url}</span>
            </a>
          </div>
        )}

        {/* Linked Content */}
        {kw.content_status === 'generating' && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">Content Generation</p>
            <div className="flex items-center gap-3 p-4 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/20 animate-pulse">
              <Loader2 className="w-5 h-5 text-indigo-500 animate-spin flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Generating blog post...</p>
                <p className="text-xs text-slate-500 mt-0.5">AI is writing an SEO-optimized article for this keyword. This takes about 20-30 seconds.</p>
              </div>
            </div>
          </div>
        )}
        {kw.content_status === 'failed' && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">Content Generation</p>
            <div className="flex items-center gap-3 p-4 rounded-lg border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/20">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-700 dark:text-red-300">Content generation failed</p>
                <p className="text-xs text-slate-500 mt-0.5">You can manually create content from the Content Dashboard.</p>
              </div>
            </div>
          </div>
        )}
        {kw.content_id && kw.content_status !== 'generating' && kw.content_status !== 'failed' && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">Generated Content</p>
            <div className="flex items-center gap-3 p-3 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/20">
              <FileText className="w-5 h-5 text-indigo-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-indigo-700 dark:text-indigo-300 truncate">
                  {kw.content_status === 'published' ? 'Published' : kw.content_status === 'humanized' ? 'Humanized Draft' : 'AI Draft'} — Blog Post
                </p>
                <p className="text-xs text-slate-500">Created when keyword was approved</p>
              </div>
              <a href={`/dashboard/content`} className="text-xs text-indigo-600 hover:underline whitespace-nowrap">
                View in Content →
              </a>
            </div>
          </div>
        )}

        {/* SERP Features */}
        {kw.serp_features && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">SERP Features</p>
            <SerpFeatureBadges features={kw.serp_features} />
          </div>
        )}

        {/* Intent */}
        {kw.intent && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">Search Intent</p>
            <Badge className={`${INTENT_CONFIG[kw.intent]?.color || 'bg-slate-100 text-slate-700'}`}>
              {INTENT_CONFIG[kw.intent]?.label || kw.intent}
            </Badge>
          </div>
        )}

        {/* Trend Data */}
        {kw.trend_data && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">Trend</p>
            <div className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700">
              {kw.trend_data.direction === 'rising' ? (
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-500" />
              )}
              <div>
                <p className="text-sm font-medium capitalize">{kw.trend_data.direction || 'Unknown'}</p>
                <p className="text-xs text-slate-500">
                  Interest: {kw.trend_data.current_interest || 0}/100 (avg {kw.trend_data.avg_interest || 0})
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Related Suggestions */}
        {kw.related_suggestions?.length > 0 && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">Related Keywords</p>
            <div className="flex flex-wrap gap-1.5">
              {kw.related_suggestions.map((s, i) => (
                <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* Position History */}
        {kw.position_history?.length > 0 && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">Position History (last {kw.position_history.length} records)</p>
            <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800 sticky top-0">
                  <tr>
                    <th className="text-left py-2 px-3 font-medium text-slate-500">Date</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-500">Position</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-500">Source</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {[...kw.position_history].reverse().map((h, i) => (
                    <tr key={i}>
                      <td className="py-1.5 px-3 text-slate-600 dark:text-slate-400">{h.date}</td>
                      <td className="py-1.5 px-3 text-right"><PositionBadge position={h.position} /></td>
                      <td className="py-1.5 px-3 text-right text-slate-400 capitalize">{h.source || 'gsc'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Notes */}
        {kw.notes && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">Notes</p>
            <p className="text-sm text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 rounded-lg p-3">{kw.notes}</p>
          </div>
        )}

        {/* Data Sources */}
        <div>
          <p className="text-xs font-medium text-slate-500 mb-2">Data Sources</p>
          <div className="flex flex-wrap gap-1.5">
            {kw.data_sources?.map((src, i) => (
              <Badge key={i} variant="outline" className="text-[10px] capitalize">{src.replace(/_/g, ' ')}</Badge>
            )) || <span className="text-xs text-slate-400">None</span>}
          </div>
          {kw.last_serp_check && (
            <p className="text-[10px] text-slate-400 mt-2">Last SERP check: {new Date(kw.last_serp_check).toLocaleDateString()}</p>
          )}
          {(kw.gsc_last_synced || kw.last_synced) && (
            <p className="text-[10px] text-slate-400">Last GSC sync: {new Date(kw.gsc_last_synced || kw.last_synced).toLocaleDateString()}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}

export default function KeywordsPage() {
  const { user } = useAuth();
  const { selectedProjectId } = useProject();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state;

  const [keywords, setKeywords] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [statusCounts, setStatusCounts] = useState({});
  const pageSize = 50;
  const [gscData, setGscData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gscLoading, setGscLoading] = useState(true);
  const aiTask = useTaskState('keywords-ai-suggestions');
  const aiLoading = aiTask.running;
  const [aiKeywordCount, setAiKeywordCount] = useState(() => {
    const saved = localStorage.getItem('gds:aiKeywordCount');
    return saved ? Number(saved) : 15;
  });

  const setAiKeywordCountPersisted = (n) => {
    setAiKeywordCount(n);
    localStorage.setItem('gds:aiKeywordCount', String(n));
  };
  const [searchQuery, setSearchQuery] = useState(navState?.search || '');
  const [activeTab, setActiveTab] = useState(navState?.filter === 'top10' ? 'tracking' : 'all');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newKeyword, setNewKeyword] = useState({ keyword: '', search_volume: '', difficulty: '', intent: 'informational', notes: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [gscConnected, setGscConnected] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [realResearchLoading, setRealResearchLoading] = useState(false);
  const serpTask = useTaskState('keywords-serp-check');
  const [gscSyncLoading, setGscSyncLoading] = useState(false);
  const [realResearchDialogOpen, setRealResearchDialogOpen] = useState(false);
  const [realSeedKeyword, setRealSeedKeyword] = useState('');
  const [realCountry, setRealCountry] = useState('US');
  const [includeDifficulty, setIncludeDifficulty] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [rankingSummary, setRankingSummary] = useState(null);
  const [researchRunsOpen, setResearchRunsOpen] = useState(false);
  const [researchRuns, setResearchRuns] = useState([]);
  const [researchRunsLoading, setResearchRunsLoading] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const autoGscSyncKeyRef = useRef(null);

  const fetchKeywords = useCallback(async () => {
    try {
      const params = {
        page,
        page_size: pageSize,
      };
      if (activeTab !== 'all') params.status = activeTab;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (selectedProjectId) params.project_id = selectedProjectId;

      const res = await api.get('/keywords/', { params });
      setKeywords(res.data.items || []);
      setTotal(res.data.total || 0);
      setHasMore(res.data.has_more || false);
      setStatusCounts(res.data.status_counts || {});
    } catch (err) {
      console.error('Failed to load keywords:', err);
    } finally {
      setLoading(false);
    }
  }, [page, activeTab, searchQuery, selectedProjectId]);

  const fetchGSCData = useCallback(async () => {
    try {
      const intRes = await api.get('/integrations/status');
      const connected = intRes.data?.gsc?.status === 'connected';
      setGscConnected(connected);
      if (connected) {
        const res = await api.get('/analytics/gsc/data', { params: { days: 28 } });
        setGscData(res.data);
      }
    } catch (err) {
      console.error('Failed to load GSC data:', err);
    } finally {
      setGscLoading(false);
    }
  }, []);

  const fetchRankingSummary = useCallback(async () => {
    try {
      const res = await api.get('/keywords/rankings-summary');
      setRankingSummary(res.data);
    } catch (err) {
      console.error('Failed to load ranking summary:', err);
    }
  }, []);

  const syncGscKeywords = useCallback(async ({ silent = false } = {}) => {
    setGscSyncLoading(true);
    try {
      const res = await api.post('/keywords/sync-from-gsc', {
        days: 28,
        project_id: selectedProjectId || undefined,
      });
      if (!silent) {
        toast.success(res.data.message || 'GSC keywords synced!');
      }
      await fetchKeywords();
      await fetchRankingSummary();
      return res.data;
    } catch (err) {
      if (silent) {
        console.error('Auto GSC keyword sync failed:', err);
      } else {
        toast.error(getErrorMessage(err, 'GSC sync failed'));
      }
      return null;
    } finally {
      setGscSyncLoading(false);
    }
  }, [fetchKeywords, fetchRankingSummary, selectedProjectId]);

  const handleViewDetail = async (kw) => {
    setDetailLoading(true);
    setSelectedKeyword(kw);
    try {
      const res = await api.get(`/keywords/${kw.keyword_id}/detail`);
      setSelectedKeyword(res.data);
    } catch (err) {
      toast.error('Failed to load keyword details');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchKeywords();
    fetchGSCData();
    fetchRankingSummary();
  }, [fetchKeywords, fetchGSCData, fetchRankingSummary]);

  useEffect(() => {
    if (loading || gscLoading || !gscConnected) return;

    const syncKey = selectedProjectId || 'default';
    if (autoGscSyncKeyRef.current === syncKey) return;

    const hasGscTopQueries = (gscData?.topQueries?.length || 0) > 0;
    const hasSyncedGscKeywords = keywords.some((kw) =>
      kw.source === 'gsc_sync' ||
      kw.gsc_last_synced ||
      kw.data_sources?.includes('gsc')
    );

    if (!hasGscTopQueries || hasSyncedGscKeywords) return;

    autoGscSyncKeyRef.current = syncKey;
    syncGscKeywords({ silent: true });
  }, [
    gscConnected,
    gscData,
    gscLoading,
    keywords,
    loading,
    selectedProjectId,
    syncGscKeywords,
  ]);

  // When AI generation finishes (running → false) and a result exists, refresh
  // the keyword list. This covers the case where generation completed while the
  // user was on another tab (component was unmounted) so the inline
  // `await fetchKeywords()` inside handleAISuggestions ran on a stale instance.
  const prevAiRunningRef = useRef(false);
  useEffect(() => {
    if (prevAiRunningRef.current && !aiTask.running && aiTask.result) {
      fetchKeywords();
      fetchRankingSummary();
    }
    prevAiRunningRef.current = aiTask.running;
  }, [aiTask.running, aiTask.result, fetchKeywords, fetchRankingSummary]);

  const hasGeneratingContent = keywords.some(k => k.content_status === 'generating');

  useEffect(() => {
    if (!hasGeneratingContent) return undefined;

    const intervalId = window.setInterval(() => {
      fetchKeywords();
    }, 10000);

    return () => window.clearInterval(intervalId);
  }, [fetchKeywords, hasGeneratingContent]);

  const handleAISuggestions = async () => {
    aiTask.start();
    try {
      const res = await api.post('/keywords/ai-suggestions', {
        project_id: selectedProjectId || undefined,
        count: aiKeywordCount,
      });
      const stats = res.data.stats;
      const msg = stats
        ? `${stats.total} keywords — ${stats.autocomplete_verified} verified, ${stats.with_volume_data} with real volume`
        : (res.data.message || 'AI suggestions generated!');
      toast.success(msg);
      aiTask.complete(res.data);
      await fetchKeywords();
    } catch (err) {
      const msg = getErrorMessage(err, 'Failed to generate suggestions');
      toast.error(msg);
      aiTask.fail(msg);
    }
  };

  const handleRealResearch = async () => {
    if (!realSeedKeyword.trim()) { toast.error('Enter a seed keyword'); return; }
    setRealResearchLoading(true);
    try {
      const res = await api.post('/keywords/real-research', {
        seed_keyword: realSeedKeyword,
        country: realCountry,
        include_trends: true,
        include_volumes: true,
        include_difficulty: includeDifficulty,
        project_id: selectedProjectId || undefined,
      });
      toast.success(res.data.message || 'Real keyword research complete!');
      setRealResearchDialogOpen(false);
      setRealSeedKeyword('');
      await fetchKeywords();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Research failed'));
    } finally {
      setRealResearchLoading(false);
    }
  };

  const handleSerpCheck = async () => {
    serpTask.start();
    try {
      const res = await api.post('/keywords/sync-serp-rankings', { country: 'US' });
      toast.success(res.data.message || 'SERP positions updated!');
      serpTask.complete(res.data);
      await fetchKeywords();
      await fetchRankingSummary();
    } catch (err) {
      const msg = getErrorMessage(err, 'SERP check failed');
      toast.error(msg);
      serpTask.fail(msg);
    }
  };

  const handleGscSync = async () => {
    await syncGscKeywords();
  };

  const handleApprove = async (keywordId) => {
    try {
      const res = await api.post(`/keywords/${keywordId}/approve/`, {
        auto_content: true,
        content_type: 'blog',
      });
      setKeywords(prev => prev.map(k => k.keyword_id === keywordId
        ? { ...k, status: 'approved', content_status: res.data.content_generating ? 'generating' : null }
        : k
      ));
      if (res.data.content_generating) {
        toast.success('Keyword approved! Content is being generated...', { duration: 4000 });
        // The hasGeneratingContent interval (every 10s) takes over from here.
      } else {
        toast.success('Keyword approved');
      }
    } catch (err) {
      toast.error('Failed to approve keyword');
    }
  };

  const handleReject = async (keywordId) => {
    try {
      await api.post(`/keywords/${keywordId}/reject/`);
      setKeywords(prev => prev.map(k => k.keyword_id === keywordId ? { ...k, status: 'rejected' } : k));
      toast.success('Keyword rejected');
    } catch (err) {
      toast.error('Failed to reject keyword');
    }
  };

  const handleDelete = async (keywordId) => {
    if (!window.confirm('Are you sure you want to delete this keyword?')) return;
    try {
      await api.delete(`/keywords/${keywordId}/`);
      setKeywords(prev => prev.filter(k => k.keyword_id !== keywordId));
      toast.success('Keyword deleted');
    } catch (err) {
      toast.error('Failed to delete keyword');
    }
  };

  const handleAddKeyword = async () => {
    if (!newKeyword.keyword.trim()) return;
    setAddLoading(true);
    try {
      const payload = {
        keyword: newKeyword.keyword.trim(),
        search_volume: newKeyword.search_volume ? parseInt(newKeyword.search_volume) : null,
        difficulty: newKeyword.difficulty ? parseInt(newKeyword.difficulty) : null,
        intent: newKeyword.intent || null,
        notes: newKeyword.notes || null,
        project_id: selectedProjectId || undefined,
      };
      await api.post('/keywords', payload);
      toast.success('Keyword added');
      setAddDialogOpen(false);
      setNewKeyword({ keyword: '', search_volume: '', difficulty: '', intent: 'informational', notes: '' });
      await fetchKeywords();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to add keyword'));
    } finally {
      setAddLoading(false);
    }
  };

  const handleSyncRankings = async () => {
    setSyncLoading(true);
    try {
      const res = await api.post('/keywords/sync-rankings');
      toast.success(res.data.message || 'Rankings synced!');
      await fetchKeywords();
      await fetchRankingSummary();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to sync rankings'));
    } finally {
      setSyncLoading(false);
    }
  };

  const handleViewResearchHistory = async () => {
    setResearchRunsOpen(true);
    setResearchRunsLoading(true);
    try {
      const res = await api.get('/keywords/research-runs', { params: { page: 1, page_size: 20 } });
      setResearchRuns(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load research history');
    } finally {
      setResearchRunsLoading(false);
    }
  };

  const handleViewHistory = async (kw) => {
    setHistoryDialogOpen(true);
    setHistoryLoading(true);
    setHistoryData(null);
    try {
      const res = await api.get(`/keywords/${kw.keyword_id}/history/`, { params: { days: 30 } });
      setHistoryData(res.data);
    } catch (err) {
      toast.error('Failed to load history');
      setHistoryDialogOpen(false);
    } finally {
      setHistoryLoading(false);
    }
  };

  const filteredKeywords = keywords;

  const counts = {
    all: statusCounts.all || 0,
    tracking: statusCounts.tracking || 0,
    suggested: statusCounts.suggested || 0,
    approved: statusCounts.approved || 0,
    rejected: statusCounts.rejected || 0,
  };

  const topQueries = gscData?.topQueries || [];

  if (loading || gscLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="keywords-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">Keywords</h1>
          <p className="text-slate-600 dark:text-slate-400">
            Track your keyword rankings and manage your SEO strategy
          </p>
        </div>
        <div className="flex items-center gap-3">
          {gscConnected && (
            <Button variant="outline" onClick={handleSyncRankings} disabled={syncLoading}>
              {syncLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              {syncLoading ? 'Syncing...' : 'Sync Rankings'}
            </Button>
          )}
          {gscConnected && (
            <Button variant="outline" onClick={handleGscSync} disabled={gscSyncLoading} title="Import all ranking keywords from Google Search Console">
              {gscSyncLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              {gscSyncLoading ? 'Syncing...' : 'Sync GSC Keywords'}
            </Button>
          )}
          <Button variant="outline" onClick={handleSerpCheck} disabled={serpTask.running} title="Check real Google SERP positions for tracked keywords">
            {serpTask.running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            {serpTask.running ? 'Checking...' : 'SERP Check'}
          </Button>
          <Button variant="outline" onClick={() => setAddDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add Keyword
          </Button>
          <Button variant="outline" onClick={() => setRealResearchDialogOpen(true)}>
            <Target className="w-4 h-4 mr-2" />
            Real Research
          </Button>
          <Button variant="outline" onClick={handleViewResearchHistory} title="View past research runs">
            <History className="w-4 h-4 mr-2" />
            Research History
          </Button>
          <div className="flex items-center gap-0">
            <select
              value={aiKeywordCount}
              onChange={(e) => setAiKeywordCountPersisted(Number(e.target.value))}
              disabled={aiLoading}
              className="h-9 rounded-l-md rounded-r-none border border-r-0 border-input bg-transparent px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
            >
              {[5, 10, 15, 20, 30, 50].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <Button className="gds-btn-primary rounded-l-none" onClick={handleAISuggestions} disabled={aiLoading}>
              {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {aiLoading ? 'Generating...' : 'AI Suggestions'}
            </Button>
          </div>
        </div>
      </div>

      {/* GSC Overview Cards */}
      {gscConnected && gscData ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <OverviewCard
            icon={Hash}
            label="Total Keywords"
            value={gscData.overview?.totalKeywords || 0}
            color="bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30"
          />
          <OverviewCard
            icon={TrendingUp}
            label="Top 10 Rankings"
            value={gscData.overview?.top10 || 0}
            color="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30"
          />
          <OverviewCard
            icon={MousePointerClick}
            label="Total Clicks"
            value={gscData.overview?.totalClicks?.toLocaleString() || 0}
            color="bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30"
          />
          <OverviewCard
            icon={Eye}
            label="Impressions"
            value={gscData.overview?.totalImpressions?.toLocaleString() || 0}
            color="bg-violet-100 text-violet-600 dark:bg-violet-900/30"
          />
          <OverviewCard
            icon={Target}
            label="Avg. Position"
            value={gscData.overview?.avgPosition ? Math.round(gscData.overview.avgPosition) : '--'}
            subtext={`CTR: ${gscData.overview?.avgCTR || 0}%`}
            color="bg-amber-100 text-amber-600 dark:bg-amber-900/30"
          />
        </div>
      ) : !gscConnected ? (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                <Plug className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-amber-900 dark:text-amber-100">Connect Google Search Console</p>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Connect GSC to see real keyword ranking data from Google
                </p>
              </div>
              <Button
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={() => navigate('/dashboard/integrations')}
              >
                <Plug className="w-4 h-4 mr-2" />
                Connect
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {/* Rankings Summary — SEOptimer-style */}
      {rankingSummary && rankingSummary.keywords_with_position > 0 && (
        <Card className="gds-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-500" />
              Organic Keyword Positions
            </CardTitle>
            <CardDescription>
              {rankingSummary.keywords_with_position} keywords with rankings · Est. {rankingSummary.estimated_monthly_traffic?.toLocaleString()} monthly traffic
              {rankingSummary.avg_ctr > 0 && ` · ${rankingSummary.avg_ctr}% avg CTR`}
              {rankingSummary.traffic_data_source === 'gsc' && (
                <Badge variant="outline" className="ml-2 text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                  <ShieldCheck className="w-3 h-3 mr-0.5" />Real GSC Data
                </Badge>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <PositionBucketBar buckets={rankingSummary.position_buckets} />

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 text-center">
                <p className="text-xs text-emerald-600 font-medium">Page 1</p>
                <p className="text-2xl font-bold text-emerald-700">
                  {(rankingSummary.position_buckets['1'] || 0) + (rankingSummary.position_buckets['2-3'] || 0) + (rankingSummary.position_buckets['4-10'] || 0)}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-indigo-50 dark:bg-indigo-950/20 text-center">
                <p className="text-xs text-indigo-600 font-medium">Est. Traffic</p>
                <p className="text-2xl font-bold text-indigo-700">{rankingSummary.estimated_monthly_traffic?.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/20 text-center">
                <p className="text-xs text-amber-600 font-medium">Avg CTR</p>
                <p className="text-2xl font-bold text-amber-700">{rankingSummary.avg_ctr || 0}%</p>
              </div>
              <div className="p-3 rounded-xl bg-cyan-50 dark:bg-cyan-950/20 text-center">
                <p className="text-xs text-cyan-600 font-medium">Total Clicks</p>
                <p className="text-2xl font-bold text-cyan-700">{rankingSummary.total_clicks?.toLocaleString()}</p>
              </div>
              <div className="p-3 rounded-xl bg-violet-50 dark:bg-violet-950/20 text-center">
                <p className="text-xs text-violet-600 font-medium">Impressions</p>
                <p className="text-2xl font-bold text-violet-700">{rankingSummary.total_impressions?.toLocaleString()}</p>
              </div>
            </div>

            {/* Top Movers */}
            {(rankingSummary.top_movers_up?.length > 0 || rankingSummary.top_movers_down?.length > 0) && (
              <div className="grid md:grid-cols-2 gap-4">
                {rankingSummary.top_movers_up?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-emerald-600 mb-2 flex items-center gap-1">
                      <ArrowUpRight className="w-3.5 h-3.5" /> Top Movers Up
                    </p>
                    <div className="space-y-1">
                      {rankingSummary.top_movers_up.slice(0, 5).map((m, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-emerald-50/50 dark:bg-emerald-950/10 text-sm">
                          <span className="truncate flex-1 font-medium">{m.keyword}</span>
                          <span className="text-emerald-600 font-bold ml-2">▲ {m.change}</span>
                          <span className="text-slate-500 ml-2 text-xs">→ #{m.current}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {rankingSummary.top_movers_down?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-red-600 mb-2 flex items-center gap-1">
                      <ArrowDownRight className="w-3.5 h-3.5" /> Top Movers Down
                    </p>
                    <div className="space-y-1">
                      {rankingSummary.top_movers_down.slice(0, 5).map((m, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-red-50/50 dark:bg-red-950/10 text-sm">
                          <span className="truncate flex-1 font-medium">{m.keyword}</span>
                          <span className="text-red-600 font-bold ml-2">▼ {Math.abs(m.change)}</span>
                          <span className="text-slate-500 ml-2 text-xs">→ #{m.current}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* GSC Top Queries Table */}
      {gscConnected && topQueries.length > 0 && (
        <Card className="gds-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Search className="w-5 h-5 text-cyan-500" />
              Search Console Keywords
            </CardTitle>
            <CardDescription>
              Top 20 keywords from Google Search Console (last 28 days)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-3 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Keyword</th>
                    <th className="text-right py-3 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Position</th>
                    <th className="text-right py-3 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Clicks</th>
                    <th className="text-right py-3 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Impressions</th>
                    <th className="text-right py-3 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">CTR</th>
                    <th className="text-right py-3 px-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Conversions (GA4)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {topQueries.map((q, i) => (
                    <motion.tr
                      key={i}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.02 }}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    >
                      <td className="py-3 px-3">
                        <span className="font-medium text-slate-800 dark:text-slate-200">{q.query}</span>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <PositionBadge position={q.position} />
                      </td>
                      <td className="py-3 px-3 text-right font-medium">{q.clicks.toLocaleString()}</td>
                      <td className="py-3 px-3 text-right text-slate-600">{q.impressions.toLocaleString()}</td>
                      <td className="py-3 px-3 text-right text-slate-600">{q.ctr}%</td>
                      <td className="py-3 px-3 text-right text-slate-600">
                        {q.conversions != null ? (
                          <span className="whitespace-nowrap" title={q.conversion_revenue != null ? `Revenue: $${Number(q.conversion_revenue).toFixed(2)}` : undefined}>
                            {Number.isInteger(q.conversions) ? q.conversions : Number(q.conversions).toFixed(1)}
                            {q.conversion_revenue != null && q.conversion_revenue > 0 && (
                              <span className="block text-[10px] text-slate-400">
                                ${Number(q.conversion_revenue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tracked Keywords Section */}
      <Card className="gds-card">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Target className="w-5 h-5 text-indigo-500" />
                Tracked Keywords
              </CardTitle>
              <CardDescription>
                {counts.all} keyword{counts.all !== 1 ? 's' : ''} total
                {counts.suggested > 0 && ` · ${counts.suggested} pending review`}
              </CardDescription>
            </div>
            <div className="relative w-full md:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search keywords..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(1); }}>
            <TabsList className="mb-4">
              <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
              <TabsTrigger value="tracking">Tracking ({counts.tracking})</TabsTrigger>
              <TabsTrigger value="suggested">Suggested ({counts.suggested})</TabsTrigger>
              <TabsTrigger value="approved">Approved ({counts.approved})</TabsTrigger>
              <TabsTrigger value="rejected">Rejected ({counts.rejected})</TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-0">
              {filteredKeywords.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                    <Target className="w-7 h-7 text-slate-400" />
                  </div>
                  <p className="font-medium text-slate-600 dark:text-slate-300 mb-1">
                    {searchQuery ? 'No keywords match your search' : 'No keywords yet'}
                  </p>
                  <p className="text-sm text-slate-500 mb-4 max-w-xs">
                    {searchQuery
                      ? 'Try a different search term'
                      : 'Add keywords manually or use AI to get suggestions'}
                  </p>
                  {!searchQuery && (
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setAddDialogOpen(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Add Keyword
                      </Button>
                      <div className="flex items-center gap-0">
                        <select
                          value={aiKeywordCount}
                          onChange={(e) => setAiKeywordCountPersisted(Number(e.target.value))}
                          disabled={aiLoading}
                          className="h-9 rounded-l-md rounded-r-none border border-r-0 border-input bg-transparent px-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
                        >
                          {[5, 10, 15, 20, 30, 50].map((n) => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                        <Button className="gds-btn-primary rounded-l-none" onClick={handleAISuggestions} disabled={aiLoading}>
                          <Sparkles className="w-4 h-4 mr-2" />
                          AI Suggestions
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredKeywords.map((kw, i) => {
                    const statusConf = STATUS_CONFIG[kw.status] || STATUS_CONFIG.tracking;
                    const intentConf = INTENT_CONFIG[kw.intent] || null;
                    return (
                      <motion.div
                        key={kw.keyword_id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.03 }}
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                        onClick={() => handleViewDetail(kw)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm text-slate-800 dark:text-slate-200">
                              {kw.keyword}
                            </p>
                            <Badge className={`text-[10px] px-1.5 py-0 ${statusConf.color}`}>
                              {statusConf.label}
                            </Badge>
                            {intentConf && (
                              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${intentConf.color}`}>
                                {intentConf.label}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                            {kw.search_volume && (
                              <span className="flex items-center gap-1">
                                Vol: {kw.search_volume.toLocaleString()}
                                {kw.volume_source && kw.volume_source !== 'ai_estimate' && (
                                  <span title={`Source: ${kw.volume_source}`} className="inline-flex items-center">
                                    <ShieldCheck className="w-3 h-3 text-emerald-500" />
                                  </span>
                                )}
                              </span>
                            )}
                            {kw.difficulty && <span>Diff: {kw.difficulty}/100</span>}
                            {kw.competition && (
                              <span className={`px-1.5 py-0 rounded text-[10px] font-medium ${
                                kw.competition === 'low' ? 'bg-emerald-50 text-emerald-600' :
                                kw.competition === 'high' ? 'bg-red-50 text-red-600' :
                                'bg-amber-50 text-amber-600'
                              }`}>
                                {kw.competition}
                              </span>
                            )}
                            {kw.cpc_estimate != null && <span>CPC: ${kw.cpc_estimate}</span>}
                            {kw.current_position && (
                              <span className="flex items-center gap-1">
                                Pos: <PositionBadge position={kw.current_position} />
                                {kw.position_history && kw.position_history.length >= 2 && (() => {
                                  const prev = kw.position_history[kw.position_history.length - 2]?.position;
                                  const curr = kw.current_position;
                                  if (prev && curr < prev) return <ArrowUpRight className="w-3 h-3 text-emerald-500" />;
                                  if (prev && curr > prev) return <ArrowDownRight className="w-3 h-3 text-red-500" />;
                                  return <Minus className="w-3 h-3 text-slate-400" />;
                                })()}
                              </span>
                            )}
                            {kw.gsc_clicks != null && <span>Clicks: {kw.gsc_clicks}</span>}
                            {kw.gsc_ctr != null && <span>CTR: {kw.gsc_ctr}%</span>}
                            {kw.estimated_traffic > 0 && (
                              <span className="flex items-center gap-1 text-indigo-600">
                                <Zap className="w-3 h-3" />~{kw.estimated_traffic}/mo
                              </span>
                            )}
                            {kw.serp_features && Object.values(kw.serp_features).some(Boolean) && (
                              <span className="flex items-center gap-1 text-amber-600">
                                <Star className="w-3 h-3" />SERP features
                              </span>
                            )}
                            {kw.position_history && kw.position_history.length > 0 && (
                              <button
                                onClick={() => handleViewHistory(kw)}
                                className="text-indigo-500 hover:underline cursor-pointer"
                              >
                                History ({kw.position_history.length})
                              </button>
                            )}
                          </div>
                          {/* Data sources row */}
                          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                            {kw.autocomplete_verified && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 font-medium">
                                <ShieldCheck className="w-2.5 h-2.5" />
                                Verified
                              </span>
                            )}
                            {(kw.data_sources?.includes('gsc') || kw.source === 'gsc_sync' || kw.gsc_last_synced || kw.gsc_ctr != null) && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 font-medium">
                                <Globe className="w-2.5 h-2.5" />
                                GSC
                              </span>
                            )}
                            {kw.data_sources?.includes('google_autocomplete') && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400 font-medium">
                                <Search className="w-2.5 h-2.5" />
                                Autocomplete
                              </span>
                            )}
                            {kw.data_sources?.includes('web_search') && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400 font-medium">
                                <Database className="w-2.5 h-2.5" />
                                Web Data
                              </span>
                            )}
                            {(!kw.data_sources || (kw.data_sources.length === 1 && kw.data_sources[0] === 'ai_suggestion')) && kw.created_by === 'ai_suggestion' && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-medium">
                                <AlertCircle className="w-2.5 h-2.5" />
                                Unverified
                              </span>
                            )}
                            {kw.notes && <span className="text-xs text-slate-400 truncate max-w-[200px]">{kw.notes}</span>}
                            {kw.content_status === 'generating' && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 font-medium animate-pulse">
                                <Loader2 className="w-2.5 h-2.5 animate-spin" />
                                Generating content...
                              </span>
                            )}
                            {kw.content_status === 'ai_generated' && kw.content_id && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 font-medium">
                                <CheckCircle2 className="w-2.5 h-2.5" />
                                Content ready
                              </span>
                            )}
                            {kw.content_status === 'published' && kw.content_id && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 font-medium">
                                <FileText className="w-2.5 h-2.5" />
                                Published
                              </span>
                            )}
                            {kw.content_status === 'failed' && (
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 font-medium">
                                <AlertCircle className="w-2.5 h-2.5" />
                                Content failed
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {kw.status === 'suggested' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                                onClick={(e) => { e.stopPropagation(); handleApprove(kw.keyword_id); }}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 text-red-600 border-red-200 hover:bg-red-50"
                                onClick={(e) => { e.stopPropagation(); handleReject(kw.keyword_id); }}
                              >
                                <XCircle className="w-3.5 h-3.5 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                            onClick={(e) => { e.stopPropagation(); handleDelete(kw.keyword_id); }}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                          <ChevronRight className="w-4 h-4 text-slate-300" />
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </Tabs>

          {/* Pagination */}
          {total > 0 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Page {page} of {Math.ceil(total / pageSize)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={!hasMore}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Keyword Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Keyword</DialogTitle>
            <DialogDescription>Add a new keyword to track in your SEO strategy.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium mb-1 block">Keyword *</label>
              <Input
                placeholder="e.g. best seo services"
                value={newKeyword.keyword}
                onChange={(e) => setNewKeyword(prev => ({ ...prev, keyword: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Search Volume</label>
                <Input
                  type="number"
                  placeholder="e.g. 1000"
                  value={newKeyword.search_volume}
                  onChange={(e) => setNewKeyword(prev => ({ ...prev, search_volume: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Difficulty (1-100)</label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  placeholder="e.g. 45"
                  value={newKeyword.difficulty}
                  onChange={(e) => setNewKeyword(prev => ({ ...prev, difficulty: e.target.value }))}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Intent</label>
              <select
                className="w-full h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm"
                value={newKeyword.intent}
                onChange={(e) => setNewKeyword(prev => ({ ...prev, intent: e.target.value }))}
              >
                <option value="informational">Informational</option>
                <option value="transactional">Transactional</option>
                <option value="navigational">Navigational</option>
                <option value="commercial">Commercial</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Notes</label>
              <Input
                placeholder="Optional notes..."
                value={newKeyword.notes}
                onChange={(e) => setNewKeyword(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
              <Button
                className="gds-btn-primary"
                onClick={handleAddKeyword}
                disabled={!newKeyword.keyword.trim() || addLoading}
              >
                {addLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Keyword
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Keyword History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Position History: {historyData?.keyword || ''}</DialogTitle>
            <DialogDescription>
              {historyData ? `Current position: #${historyData.current_position || '--'}` : 'Loading...'}
            </DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
            </div>
          ) : historyData?.history?.length > 0 ? (
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-2 px-2 text-xs font-medium text-slate-500">Date</th>
                    <th className="text-right py-2 px-2 text-xs font-medium text-slate-500">Position</th>
                    <th className="text-right py-2 px-2 text-xs font-medium text-slate-500">Clicks</th>
                    <th className="text-right py-2 px-2 text-xs font-medium text-slate-500">Impressions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {[...historyData.history].reverse().map((h, i) => (
                    <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="py-2 px-2 text-slate-700 dark:text-slate-300">{h.date}</td>
                      <td className="py-2 px-2 text-right"><PositionBadge position={h.position} /></td>
                      <td className="py-2 px-2 text-right">{h.clicks ?? '--'}</td>
                      <td className="py-2 px-2 text-right">{h.impressions?.toLocaleString() ?? '--'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-slate-500 py-8 text-center">No history data yet. Sync rankings first.</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Real Keyword Research Dialog */}
      <Dialog open={realResearchDialogOpen} onOpenChange={setRealResearchDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Target className="h-5 w-5 text-indigo-500" /> Real Keyword Research</DialogTitle>
            <DialogDescription>Discover keywords from Google Autocomplete, Trends, and web search — all real data.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-emerald-50 dark:bg-emerald-950/20 rounded-lg p-3 text-xs text-emerald-700 dark:text-emerald-400">
              Uses Google Autocomplete (real suggestions), Google Trends (real trend data), and live web search (real volume estimates).
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Seed Keyword</label>
              <Input placeholder="e.g. IT services Dallas" value={realSeedKeyword} onChange={e => setRealSeedKeyword(e.target.value)} className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Country</label>
              <select value={realCountry} onChange={e => setRealCountry(e.target.value)} className="mt-1 w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-900 dark:border-slate-700 text-sm">
                <option value="US">United States</option>
                <option value="GB">United Kingdom</option>
                <option value="CA">Canada</option>
                <option value="AU">Australia</option>
                <option value="IN">India</option>
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={includeDifficulty} onChange={e => setIncludeDifficulty(e.target.checked)} className="rounded" />
              Include difficulty analysis (analyzes top 10 SERP — takes longer)
            </label>
            <Button onClick={handleRealResearch} disabled={realResearchLoading} className="w-full gap-2">
              {realResearchLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Researching real data...</> : <><Target className="h-4 w-4" /> Research with Real Data</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Research History Dialog */}
      <Dialog open={researchRunsOpen} onOpenChange={setResearchRunsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-500" /> Research History
            </DialogTitle>
            <DialogDescription>Past keyword research runs</DialogDescription>
          </DialogHeader>
          {researchRunsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
            </div>
          ) : researchRuns.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">No research runs yet. Use "Real Research" to get started.</p>
          ) : (
            <div className="max-h-96 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
              {researchRuns.map((run) => (
                <div key={run.run_id} className="py-3 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm text-slate-800 dark:text-slate-200 truncate">
                      "{run.inputs?.seed_keyword}"
                    </span>
                    <Badge variant="outline" className="text-[10px] flex-shrink-0">
                      {run.inputs?.country || 'US'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span>{new Date(run.created_at).toLocaleString()}</span>
                    <span>·</span>
                    <span className="text-indigo-600 font-medium">{run.outputs?.total_suggestions || 0} suggestions</span>
                    {run.duration_ms && (
                      <>
                        <span>·</span>
                        <span>{(run.duration_ms / 1000).toFixed(1)}s</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Keyword Detail Slide-Over Panel */}
      <AnimatePresence>
        {selectedKeyword && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.3 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black z-40"
              onClick={() => setSelectedKeyword(null)}
            />
            <KeywordDetailPanel
              keyword={selectedKeyword}
              onClose={() => setSelectedKeyword(null)}
            />
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
